import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const sovButtonVariants = cva(
  'inline-flex items-center justify-center gap-2 rounded-lg font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-950 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        primary:  'bg-brand text-zinc-950 hover:bg-brand-light active:bg-brand-dark shadow-lg hover:shadow-xl hover:shadow-brand/20',
        secondary:'bg-blue-700 text-white hover:bg-blue-600 active:bg-blue-800 shadow-lg',
        outline:  'border-2 border-brand text-brand hover:bg-brand/10 active:bg-brand/20',
        ghost:    'text-slate-300 hover:bg-slate-800 hover:text-slate-50 active:bg-slate-700',
        danger:   'bg-red-600 text-white hover:bg-red-700 active:bg-red-800 shadow-lg',
        success:  'bg-green-600 text-white hover:bg-green-700 active:bg-green-800 shadow-lg',
      },
      size: {
        sm: 'h-9 px-4 text-sm',
        md: 'h-11 px-6 text-base',
        lg: 'h-13 px-8 text-lg',
        icon: 'h-10 w-10',
      },
      fullWidth: {
        true: 'w-full',
        false: '',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
      fullWidth: false,
    },
  }
);

export interface SovButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof sovButtonVariants> {
  loading?: boolean;
}

export const SovButton = React.forwardRef<HTMLButtonElement, SovButtonProps>(
  ({ className, variant, size, fullWidth, loading, disabled, children, ...props }, ref) => (
    <button
      className={cn(sovButtonVariants({ variant, size, fullWidth, className }))}
      ref={ref}
      disabled={disabled || loading}
      {...props}
    >
      {loading && (
        <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
      )}
      {children}
    </button>
  )
);
SovButton.displayName = 'SovButton';
